import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import { useLanguage } from '../contexts/LanguageContext';

interface Coupon {
  id: number;
  title: string;
  description: string;
  discount: string;
  vendor_name: string;
  vendor_address: string;
  vendor_category: string;
  driver_name: string;
  max_uses: number;
  current_uses: number;
}

interface GeneratedQR {
  token: string;
  qrData: string;
  qrImageUrl: string;
  coupon: {
    id: number;
    title: string;
    description: string;
    discount: string;
    vendor_name: string;
    expires_in_minutes: number;
  };
}

export const MobileGeneratePage: React.FC = () => {
  const { t } = useLanguage();
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [selectedCoupon, setSelectedCoupon] = useState<number | null>(null);
  const [customerPhone, setCustomerPhone] = useState('');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [qrData, setQrData] = useState<GeneratedQR | null>(null);
  const [showQRModal, setShowQRModal] = useState(false);

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      const response = await fetch('https://cf.qrsp.app/api/coupons');
      const data = await response.json();
      setCoupons(data.coupons || []);
    } catch (error) {
      console.error('Failed to fetch coupons:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateQR = async (couponId: number) => {
    setGenerating(true);
    
    try {
      const response = await fetch('https://cf.qrsp.app/api/qr/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          coupon_id: couponId,
          customer_phone: customerPhone || undefined,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to generate QR code');
      }

      // Generate QR code image
      const qrImageUrl = await QRCode.toDataURL(data.token, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#ffffff',
        },
      });

      const qrResult = { ...data, qrImageUrl };
      setQrData(qrResult);
      setShowQRModal(true);

      // Haptic feedback
      if (navigator.vibrate) {
        navigator.vibrate([100, 50, 100]);
      }

    } catch (error) {
      alert(t('generate.error') + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setGenerating(false);
    }
  };

  const closeQRModal = () => {
    setShowQRModal(false);
    setQrData(null);
    setSelectedCoupon(null);
    setCustomerPhone('');
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'restaurant': return '🍽️';
      case 'pharmacy': return '💊';
      case 'retail': return '🛒';
      case 'services': return '🔧';
      default: return '🏪';
    }
  };

  const getUsageColor = (current: number, max: number) => {
    const percentage = (current / max) * 100;
    if (percentage >= 90) return 'text-red-600';
    if (percentage >= 70) return 'text-yellow-600';
    return 'text-green-600';
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('generate.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white p-4 shadow-sm">
        <h1 className="text-xl font-bold text-gray-900">{t('generate.title')}</h1>
        <p className="text-gray-600 text-sm">{t('generate.subtitle')}</p>
      </div>

      {/* Customer Phone Input */}
      <div className="bg-white p-4 border-b">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('generate.phone.label')}
        </label>
        <input
          type="tel"
          value={customerPhone}
          onChange={(e) => setCustomerPhone(e.target.value)}
          placeholder={t('generate.phone.placeholder')}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 text-lg"
        />
      </div>

      {/* Coupons List */}
      <div className="flex-1 overflow-y-auto">
        {coupons.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center p-8">
              <div className="text-6xl mb-4">📭</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('generate.no.coupons')}</h3>
              <p className="text-gray-600">{t('generate.no.coupons.desc')}</p>
            </div>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            {coupons.map((coupon) => (
              <div
                key={coupon.id}
                className="bg-white rounded-xl p-4 shadow-sm border border-gray-200"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="text-xl">{getCategoryIcon(coupon.vendor_category)}</span>
                      <h3 className="font-bold text-gray-900">{coupon.title}</h3>
                    </div>
                    <p className="text-sm text-gray-600 mb-1">{coupon.vendor_name}</p>
                    <p className="text-xs text-gray-500">{coupon.vendor_address}</p>
                  </div>
                  <div className="text-right">
                    <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-bold">
                      {coupon.discount}
                    </div>
                  </div>
                </div>

                {coupon.description && (
                  <p className="text-sm text-gray-700 mb-3">{coupon.description}</p>
                )}

                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-4 text-xs text-gray-500">
                    <span>{t('generate.driver')} {coupon.driver_name}</span>
                    <span className={getUsageColor(coupon.current_uses, coupon.max_uses)}>
                      {t('generate.usage')} {coupon.current_uses}/{coupon.max_uses} {t('generate.used')}
                    </span>
                  </div>
                </div>

                <button
                  onClick={() => generateQR(coupon.id)}
                  disabled={generating || coupon.current_uses >= coupon.max_uses}
                  className={`w-full py-3 rounded-xl font-bold transition-all ${
                    coupon.current_uses >= coupon.max_uses
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : generating
                      ? 'bg-gray-400 text-white'
                      : 'bg-green-600 hover:bg-green-700 text-white'
                  }`}
                >
                  {generating ? (
                    <span className="flex items-center justify-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>{t('generate.generating')}</span>
                    </span>
                  ) : coupon.current_uses >= coupon.max_uses ? (
                    t('generate.exhausted')
                  ) : (
                    t('generate.button')
                  )}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* QR Code Modal */}
      {showQRModal && qrData && (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
            <div className="text-center">
              <div className="mb-4">
                <img
                  src={qrData.qrImageUrl}
                  alt="QR Code"
                  className="mx-auto border-2 border-gray-200 rounded-lg"
                />
              </div>
              
              <h3 className="font-bold text-lg mb-1">{qrData.coupon.title}</h3>
              <p className="text-gray-600 text-sm mb-2">{qrData.coupon.vendor_name}</p>
              <div className="bg-green-100 text-green-800 px-4 py-2 rounded-full text-lg font-bold mb-4">
                {qrData.coupon.discount}
              </div>
              
              {qrData.coupon.description && (
                <p className="text-sm text-gray-700 mb-4">{qrData.coupon.description}</p>
              )}

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-6">
                <p className="text-xs text-yellow-800 font-medium">
                  {t('generate.expires', { minutes: qrData.coupon.expires_in_minutes })}
                </p>
                <p className="text-xs text-yellow-700 mt-1">
                  {t('generate.show.desc')}
                </p>
              </div>

              <div className="space-y-3">
                <button
                  onClick={closeQRModal}
                  className="w-full bg-green-600 text-white py-3 rounded-xl font-bold"
                >
                  {t('generate.success')}
                </button>
                
                <button
                  onClick={() => generateQR(qrData.coupon.id)}
                  className="w-full bg-gray-100 text-gray-700 py-3 rounded-xl font-medium"
                  disabled={generating}
                >
                  {t('generate.new')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};